/**
 * Accessibility Fixes
 * Addresses WCAG 2.1 AA issues identified in accessibility audit
 * - Focus Not Obscured (WCAG 2.4.11) - Cart drawer focus management
 * - Name, Role, Value (WCAG 4.1.2) - Accessible names for elements
 * - Target Size (WCAG 2.5.8) - Minimum touch target sizes
 * - Hover/Focus Content (WCAG 1.4.13) - Dismissible overlays
 * - Status Messages (WCAG 4.1.3) - ARIA live regions
 */

(function() {
  'use strict';

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    fixCartDrawerFocus();
    fixDisclosureMenus();
    fixJudgeMeAccessibility();
    fixStatusMessages();
    fixInteractiveElements();
    fixTargetSizes();
    fixThirdPartyElements();
    fixSVGAccessibility();
    fixUnclearLinks();
    fixExternalLinks();
  }

  /**
   * Fix 1: Disclosure menus - Escape key dismissal (WCAG 1.4.13)
   * Ensures hover/focus content can be dismissed with Escape key
   */
  function fixDisclosureMenus() {
    // Handle mega menu details elements
    const megaMenus = document.querySelectorAll('.mega-menu, .disclosure, details[id^="Details-"]');

    megaMenus.forEach(menu => {
      const details = menu.tagName === 'DETAILS' ? menu : menu.querySelector('details');
      if (!details) return;

      // Add Escape key handler
      details.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && details.open) {
          e.preventDefault();
          details.open = false;
          const summary = details.querySelector('summary');
          if (summary) summary.focus();
        }
      });

      // Ensure content is dismissible by clicking outside
      document.addEventListener('click', function(e) {
        if (details.open && !details.contains(e.target)) {
          details.open = false;
        }
      });
    });

    // Handle disclosure buttons (country/language selectors)
    const disclosureWrappers = document.querySelectorAll('.disclosure__list-wrapper');

    disclosureWrappers.forEach(wrapper => {
      const parent = wrapper.closest('.disclosure');
      if (!parent) return;

      parent.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && !wrapper.hidden) {
          e.preventDefault();
          wrapper.hidden = true;
          const button = parent.querySelector('.disclosure__button');
          if (button) {
            button.setAttribute('aria-expanded', 'false');
            button.focus();
          }
        }
      });
    });
  }

  /**
   * Fix 2: Judge.me accessibility fixes
   * Adds accessible names to Judge.me elements that lack them
   */
  function fixJudgeMeAccessibility() {
    // Run immediately and after delays for slow-loading Judge.me scripts
    applyJudgeMeFixes();
    setTimeout(applyJudgeMeFixes, 2000);
    setTimeout(applyJudgeMeFixes, 5000);
  }

  function applyJudgeMeFixes() {
    // Fix star rating elements - both inputs and clickable stars
    const starInputs = document.querySelectorAll('.jdgm-write-review-modal__stars input, .jdgm-rev__rating input');
    starInputs.forEach(input => {
      if (!input.getAttribute('aria-label')) {
        input.setAttribute('aria-label', 'Review rating');
      }
    });

    // Fix star rating buttons/labels
    const starLabels = document.querySelectorAll('.jdgm-star, [class*="jdgm-star"]');
    starLabels.forEach((star, index) => {
      if (!star.getAttribute('aria-label')) {
        star.setAttribute('aria-label', `${index + 1} star rating`);
        star.setAttribute('role', 'radio');
      }
    });

    // Fix review form inputs - expanded list
    const reviewInputs = {
      '#jdgm-review-body-input': 'Review body',
      '#jdgm-review-title-input': 'Review title',
      '#jdgm-email-input': 'Email address',
      '#jdgm-name-input': 'Your name',
      '.jdgm-form__body-input': 'Review body',
      '.jdgm-form__title-input': 'Review title',
      '.jdgm-form__email-input': 'Email address',
      '.jdgm-form__name-input': 'Your name',
      '.jdgm-rev-widg__search-input': 'Search reviews',
      '.jdgm-search-input': 'Search reviews'
    };

    Object.entries(reviewInputs).forEach(([selector, label]) => {
      document.querySelectorAll(selector).forEach(input => {
        if (!input.getAttribute('aria-label')) {
          input.setAttribute('aria-label', label);
        }
      });
    });

    // Fix popup card close buttons
    const closeButtons = document.querySelectorAll('.jdgm-popup-card__close-btn, .jdgm-modal__close-btn, .jdgm-close-btn');
    closeButtons.forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Close');
        btn.setAttribute('role', 'button');
        if (!btn.hasAttribute('tabindex')) {
          btn.setAttribute('tabindex', '0');
        }
      }
    });

    // Fix full review links and buttons
    const reviewLinks = document.querySelectorAll('.jdgm-full-rev__product-button, .jdgm-popup-card__full-rev-link, .jdgm-rev__read-more');
    reviewLinks.forEach(link => {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        link.setAttribute('aria-label', 'View full review');
      }
    });

    // Fix medals link
    const medalLinks = document.querySelectorAll('.jm-medals__medal-link, .jdgm-medal-link');
    medalLinks.forEach(link => {
      if (!link.getAttribute('aria-label')) {
        link.setAttribute('aria-label', 'View store reviews on Judge.me');
      }
    });

    // Fix social share buttons
    const socialButtons = document.querySelectorAll('.jdgm-share-btn, [class*="jdgm-share"]');
    socialButtons.forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        const platform = btn.className.match(/facebook|twitter|pinterest|email/i);
        const platformName = platform ? platform[0] : 'social media';
        btn.setAttribute('aria-label', `Share on ${platformName}`);
      }
    });

    // Fix pagination buttons
    const paginationBtns = document.querySelectorAll('.jdgm-paginate__page, .jdgm-paginate__prev, .jdgm-paginate__next');
    paginationBtns.forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        if (btn.classList.contains('jdgm-paginate__prev')) {
          btn.setAttribute('aria-label', 'Previous page of reviews');
        } else if (btn.classList.contains('jdgm-paginate__next')) {
          btn.setAttribute('aria-label', 'Next page of reviews');
        } else {
          const pageNum = btn.textContent.trim();
          btn.setAttribute('aria-label', `Go to page ${pageNum} of reviews`);
        }
      }
    });

    // Fix sort/filter dropdowns
    const sortSelects = document.querySelectorAll('.jdgm-sort-select, .jdgm-rev-widg__sort-dropdown');
    sortSelects.forEach(select => {
      if (!select.getAttribute('aria-label')) {
        select.setAttribute('aria-label', 'Sort reviews by');
      }
    });

    // Fix popup widget - make it dismissible with Escape
    const popupWidgets = document.querySelectorAll('.jdgm-popup-widget-wrapper, .jdgm-modal, [class*="jdgm-popup"]');
    popupWidgets.forEach(popup => {
      if (!popup.hasAttribute('data-escape-listener')) {
        popup.setAttribute('data-escape-listener', 'true');
        popup.addEventListener('keydown', function(e) {
          if (e.key === 'Escape') {
            const closeBtn = popup.querySelector('.jdgm-popup-card__close-btn, .jdgm-modal__close-btn, .jdgm-close-btn');
            if (closeBtn) closeBtn.click();
          }
        });
      }
    });

    // Fix write review button
    const writeReviewBtns = document.querySelectorAll('.jdgm-write-rev-link, .jdgm-rev-widg__summary-writerev');
    writeReviewBtns.forEach(btn => {
      if (!btn.getAttribute('aria-label') && !btn.textContent.trim()) {
        btn.setAttribute('aria-label', 'Write a review');
      }
    });

    // Fix helpful/unhelpful vote buttons
    const voteBtns = document.querySelectorAll('.jdgm-rev__vote-btn, [class*="jdgm-vote"]');
    voteBtns.forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        const isHelpful = btn.className.includes('up') || btn.className.includes('helpful');
        btn.setAttribute('aria-label', isHelpful ? 'Mark review as helpful' : 'Mark review as not helpful');
      }
    });
  }

  /**
   * Fix 3: Status messages - Add ARIA live regions (WCAG 4.1.3)
   */
  function fixStatusMessages() {
    // Cart notification
    const cartNotification = document.querySelector('.cart-notification-wrapper, #cart-notification');
    if (cartNotification && !cartNotification.getAttribute('role')) {
      cartNotification.setAttribute('role', 'status');
      cartNotification.setAttribute('aria-live', 'polite');
    }

    // Form success/error messages
    const formMessages = document.querySelectorAll('.form__message, .newsletter-form__message');
    formMessages.forEach(msg => {
      if (!msg.getAttribute('role')) {
        const isError = msg.classList.contains('form__message--error') ||
                        msg.textContent.toLowerCase().includes('error');
        msg.setAttribute('role', isError ? 'alert' : 'status');
        msg.setAttribute('aria-live', isError ? 'assertive' : 'polite');
      }
    });

    // Product form error messages
    const productErrors = document.querySelectorAll('.product-form__error-message');
    productErrors.forEach(err => {
      if (!err.getAttribute('role')) {
        err.setAttribute('role', 'alert');
        err.setAttribute('aria-live', 'assertive');
      }
    });
  }

  /**
   * Fix 4: Interactive elements lacking accessible names
   */
  function fixInteractiveElements() {
    // Newsletter form input
    const newsletterInputs = document.querySelectorAll('input[id^="NewsletterForm"]');
    newsletterInputs.forEach(input => {
      if (!input.getAttribute('aria-label')) {
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (!label) {
          input.setAttribute('aria-label', 'Email address for newsletter');
        }
      }
    });

    // Search inputs
    const searchInputs = document.querySelectorAll('input[type="search"]:not([aria-label])');
    searchInputs.forEach(input => {
      const placeholder = input.getAttribute('placeholder');
      if (placeholder) {
        input.setAttribute('aria-label', placeholder);
      } else {
        input.setAttribute('aria-label', 'Search');
      }
    });
  }

  /**
   * Fix 5: Cart Drawer Focus Management (WCAG 2.4.11)
   * Ensures focus is not obscured when cart drawer is open
   */
  function fixCartDrawerFocus() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Ensure cart drawer properly manages focus when opened
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.attributeName === 'class') {
          const isActive = cartDrawer.classList.contains('active');
          const drawerInner = cartDrawer.querySelector('.drawer__inner');

          if (isActive && drawerInner) {
            // Ensure the drawer is above other content
            drawerInner.style.zIndex = '9999';

            // Set inert on main content to prevent focus going behind drawer
            const mainContent = document.getElementById('MainContent');
            const header = document.querySelector('.section-header');

            if (mainContent) mainContent.setAttribute('inert', '');
            if (header) header.setAttribute('inert', '');

            // Ensure drawer has proper focus containment
            drawerInner.setAttribute('aria-modal', 'true');
          } else {
            // Remove inert when drawer closes
            const mainContent = document.getElementById('MainContent');
            const header = document.querySelector('.section-header');

            if (mainContent) mainContent.removeAttribute('inert');
            if (header) header.removeAttribute('inert');
          }
        }
      });
    });

    observer.observe(cartDrawer, { attributes: true });

    // Add aria-label to cart drawer note textarea if missing
    const cartNote = document.getElementById('CartDrawer-Note');
    if (cartNote && !cartNote.getAttribute('aria-label')) {
      cartNote.setAttribute('aria-label', 'Order notes');
    }
  }

  /**
   * Fix 6: Target Size Minimum (WCAG 2.5.8)
   * Ensures interactive elements meet 24x24px minimum
   */
  function fixTargetSizes() {
    // Add CSS for minimum touch targets
    if (!document.getElementById('a11y-target-size-styles')) {
      const style = document.createElement('style');
      style.id = 'a11y-target-size-styles';
      style.textContent = `
        /* Ensure minimum 24x24px touch targets (WCAG 2.5.8) */
        .country-selector__close-button,
        .header__icon--account,
        .disclosure__button,
        button.link[aria-label] {
          min-width: 44px;
          min-height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        /* Ensure focus is visible above overlays */
        .drawer__inner *:focus {
          outline: 2px solid currentColor;
          outline-offset: 2px;
          z-index: 1;
        }

        /* Ensure cart drawer is above all content when active */
        cart-drawer.active .drawer__inner {
          z-index: 9999;
        }

        /* Prevent focus on elements behind modal */
        [inert] {
          pointer-events: none;
        }
        [inert] * {
          user-select: none;
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Fix 7: Third-party elements (Judge.me arrows, QQ video toggle)
   * Adds accessible names to third-party app elements
   */
  function fixThirdPartyElements() {
    // Run immediately and with delays for slow-loading scripts
    applyThirdPartyFixes();
    setTimeout(applyThirdPartyFixes, 1000);
    setTimeout(applyThirdPartyFixes, 3000);
    setTimeout(applyThirdPartyFixes, 5000);
  }

  function applyThirdPartyFixes() {
    // Judge.me arrow buttons (carousel navigation)
    const jdgmArrows = document.querySelectorAll('.jdgm-arrow, [class*="jdgm-arrow"]');
    jdgmArrows.forEach((arrow) => {
      if (!arrow.getAttribute('aria-label')) {
        // Determine direction from class or position
        const isNext = arrow.classList.contains('jdgm-arrow-right') ||
                       arrow.classList.contains('next') ||
                       arrow.nextElementSibling === null;
        arrow.setAttribute('aria-label', isNext ? 'Next review' : 'Previous review');
        arrow.setAttribute('role', 'button');
        if (!arrow.hasAttribute('tabindex')) {
          arrow.setAttribute('tabindex', '0');
        }
      }
    });

    // QQ Video toggle button
    const qqToggle = document.querySelectorAll('.qq-toggle-btn, .qq-videos-toggle button, [class*="qq-toggle"]');
    qqToggle.forEach(btn => {
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Toggle video gallery');
        btn.setAttribute('role', 'button');
      }
    });

    // Judge.me popup widget - ensure it can be dismissed
    const jdgmPopups = document.querySelectorAll('.jdgm-popup-widget, .jdgm-popup-widget-wrapper');
    jdgmPopups.forEach(popup => {
      if (!popup.hasAttribute('data-a11y-escape')) {
        popup.setAttribute('data-a11y-escape', 'true');

        // Add Escape key to dismiss
        popup.addEventListener('keydown', function(e) {
          if (e.key === 'Escape') {
            const closeBtn = popup.querySelector('[class*="close"], .jdgm-close');
            if (closeBtn) {
              closeBtn.click();
            } else {
              // Try to hide the popup directly
              popup.style.display = 'none';
            }
          }
        });

        // Make popup focusable so it can receive keyboard events
        if (!popup.hasAttribute('tabindex')) {
          popup.setAttribute('tabindex', '-1');
        }
      }
    });

    // Judge.me popup cards - add close button accessibility
    const popupCards = document.querySelectorAll('.jdgm-popup-card');
    popupCards.forEach(card => {
      const closeBtn = card.querySelector('.jdgm-popup-card__close-btn, [class*="close"]');
      if (closeBtn && !closeBtn.getAttribute('aria-label')) {
        closeBtn.setAttribute('aria-label', 'Close review popup');
        closeBtn.setAttribute('role', 'button');
      }
    });

    // Announcement bar - add status role for screen readers
    const announcementMessages = document.querySelectorAll('.announcement-bar__message');
    announcementMessages.forEach(msg => {
      const parent = msg.closest('.announcement-bar, [role="region"]');
      if (parent && !parent.querySelector('[aria-live]')) {
        // Only add aria-live if there's a slider (multiple announcements)
        const slider = parent.closest('slideshow-component');
        if (slider) {
          const sliderContent = slider.querySelector('.slider');
          if (sliderContent && !sliderContent.getAttribute('aria-live')) {
            sliderContent.setAttribute('aria-live', 'polite');
            sliderContent.setAttribute('aria-atomic', 'true');
          }
        }
      }
    });

    // Mega menu - ensure dismissible with Escape (WCAG 1.4.13)
    const megaMenus = document.querySelectorAll('.mega-menu__content, [id^="MegaMenu-Content"]');
    megaMenus.forEach(menu => {
      if (!menu.hasAttribute('data-a11y-escape')) {
        menu.setAttribute('data-a11y-escape', 'true');

        const details = menu.closest('details');
        if (details) {
          details.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && details.open) {
              e.preventDefault();
              e.stopPropagation();
              details.open = false;
              const summary = details.querySelector('summary');
              if (summary) {
                summary.setAttribute('aria-expanded', 'false');
                summary.focus();
              }
            }
          });
        }
      }
    });

    // Localization disclosure buttons - ensure they stay visible when hovered
    const disclosureButtons = document.querySelectorAll('.disclosure__button');
    disclosureButtons.forEach(btn => {
      const listWrapper = btn.parentElement.querySelector('.disclosure__list-wrapper');
      if (listWrapper && !listWrapper.hasAttribute('data-a11y-hover')) {
        listWrapper.setAttribute('data-a11y-hover', 'true');

        // Keep open when hovering over the dropdown
        listWrapper.addEventListener('mouseenter', function() {
          listWrapper.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
        });

        // Allow dismissal by clicking outside
        document.addEventListener('click', function(e) {
          if (!btn.contains(e.target) && !listWrapper.contains(e.target)) {
            listWrapper.hidden = true;
            btn.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });
  }

/**
   * Fix 8: SVG Accessibility (WCAG 1.1.1)
   * Ensures SVGs have proper accessible names or are hidden from AT
   */
  function fixSVGAccessibility() {
    const svgs = document.querySelectorAll('svg');

    svgs.forEach(svg => {
      // Skip if already has accessibility attributes
      if (svg.getAttribute('aria-hidden') === 'true' || svg.querySelector('title')) {
        return;
      }

      // Check if SVG is inside an interactive element with accessible name
      const parent = svg.closest('a, button, [role="button"], [role="link"]');

      if (parent) {
        // Parent has accessible name - hide SVG from AT
        const parentLabel = parent.getAttribute('aria-label') ||
                           parent.getAttribute('aria-labelledby') ||
                           parent.textContent.trim();

        if (parentLabel) {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          return;
        }
      }

      // SVG needs its own accessible name
      // Try to determine name from class or context
      const svgClass = svg.className.baseVal || svg.getAttribute('class') || '';
      let iconName = '';

      // Extract icon name from common class patterns
      const iconMatch = svgClass.match(/icon[_-]?(\w+)/i);
      if (iconMatch) {
        iconName = iconMatch[1].replace(/[-_]/g, ' ');
      }

      // Map common icon names to descriptive labels
      const iconLabels = {
        'cart': 'Shopping cart',
        'search': 'Search',
        'account': 'Account',
        'menu': 'Menu',
        'hamburger': 'Menu',
        'close': 'Close',
        'arrow': 'Arrow',
        'caret': 'Expand',
        'plus': 'Add',
        'minus': 'Remove',
        'play': 'Play',
        'pause': 'Pause',
        'share': 'Share',
        'error': 'Error',
        'success': 'Success',
        'checkmark': 'Checkmark',
        'check': 'Checkmark',
        'facebook': 'Facebook',
        'instagram': 'Instagram',
        'twitter': 'Twitter',
        'pinterest': 'Pinterest',
        'youtube': 'YouTube',
        'tiktok': 'TikTok',
        'zoom': 'Zoom',
        'heart': 'Favorite',
        'star': 'Rating',
        'truck': 'Shipping',
        'lock': 'Secure',
        'filter': 'Filter',
        'eye': 'View'
      };

      const label = iconLabels[iconName.toLowerCase()] || iconName;

      if (label) {
        // Add title element for accessibility
        const existingTitle = svg.querySelector('title');
        if (!existingTitle) {
          const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
          title.textContent = label;
          svg.insertBefore(title, svg.firstChild);

          // Add aria-labelledby for screen readers
          const titleId = 'svg-title-' + Math.random().toString(36).substr(2, 9);
          title.id = titleId;
          svg.setAttribute('aria-labelledby', titleId);
        }
        svg.setAttribute('role', 'img');
      } else {
        // If we can't determine purpose, mark as decorative
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      }
    });

  }

  /**
   * Fix 9: Unclear Link Accessible Names (WCAG 2.4.4)
   * Adds context to links with vague text like "Read more", "Click here"
   */
  function fixUnclearLinks() {
    // Common vague link texts that need context
    const vagueTexts = [
      'read more',
      'click here',
      'learn more',
      'more',
      'here',
      'view',
      'details',
      'info',
      'see more',
      'view more',
      'shop now',
      'buy now',
      'add to cart'
    ];

    const links = document.querySelectorAll('a:not([aria-label]):not([aria-labelledby])');

    links.forEach(link => {
      const linkText = link.textContent.trim().toLowerCase();

      // Check if link text is vague
      const isVague = vagueTexts.some(vague => linkText === vague || linkText.startsWith(vague + ' '));

      if (isVague || linkText.length < 3) {
        // Try to find context from surrounding elements
        let context = '';

        // Check for associated heading
        const card = link.closest('.card, .product-card, .collection-card, .article-card, article, .grid__item');
        if (card) {
          const heading = card.querySelector('h1, h2, h3, h4, h5, h6, .card__heading, .card__title');
          if (heading) {
            context = heading.textContent.trim();
          }
        }

        // Check for product title
        if (!context) {
          const productTitle = link.closest('[data-product-title]');
          if (productTitle) {
            context = productTitle.getAttribute('data-product-title');
          }
        }

        // Check for aria-describedby potential
        if (!context) {
          const figcaption = link.closest('figure')?.querySelector('figcaption');
          if (figcaption) {
            context = figcaption.textContent.trim();
          }
        }

        // Build accessible label
        if (context) {
          const originalText = link.textContent.trim();
          const label = `${originalText}: ${context}`;
          link.setAttribute('aria-label', label);
        }
      }

      // Fix links that are images/icons only with no text
      if (!linkText && !link.getAttribute('aria-label')) {
        const img = link.querySelector('img');
        const svg = link.querySelector('svg');

        if (img && img.alt) {
          link.setAttribute('aria-label', img.alt);
        } else if (svg) {
          const title = svg.querySelector('title');
          if (title) {
            link.setAttribute('aria-label', title.textContent);
          } else {
            // Try to determine from URL or class
            const href = link.getAttribute('href') || '';
            if (href.includes('facebook')) link.setAttribute('aria-label', 'Facebook');
            else if (href.includes('instagram')) link.setAttribute('aria-label', 'Instagram');
            else if (href.includes('twitter') || href.includes('x.com')) link.setAttribute('aria-label', 'Twitter');
            else if (href.includes('youtube')) link.setAttribute('aria-label', 'YouTube');
            else if (href.includes('pinterest')) link.setAttribute('aria-label', 'Pinterest');
            else if (href.includes('tiktok')) link.setAttribute('aria-label', 'TikTok');
            else if (href.includes('mailto:')) link.setAttribute('aria-label', 'Send email');
            else if (href.includes('tel:')) link.setAttribute('aria-label', 'Call phone number');
          }
        }
      }
    });
  }

  /**
   * Fix 10: External Links Warning (WCAG 3.2.5)
   * Adds visual and accessible indication that links open in new tab/window
   */
  function fixExternalLinks() {
    // Find all links that open in new tabs/windows
    const externalLinks = document.querySelectorAll('a[target="_blank"]');

    externalLinks.forEach(link => {
      // Skip if already has warning
      if (link.hasAttribute('data-external-warning') ||
          link.querySelector('.visually-hidden, .sr-only')) {
        return;
      }

      // Mark as processed
      link.setAttribute('data-external-warning', 'true');

      // Get current aria-label or text
      const currentLabel = link.getAttribute('aria-label') || link.textContent.trim();

      // Skip if already mentions "new tab" or "new window"
      if (currentLabel.toLowerCase().includes('new tab') ||
          currentLabel.toLowerCase().includes('new window') ||
          currentLabel.toLowerCase().includes('opens in')) {
        return;
      }

      // Add accessible warning text (hidden visually but read by screen readers)
      const srText = document.createElement('span');
      srText.className = 'visually-hidden';
      srText.textContent = ' (opens in new tab)';
      link.appendChild(srText);

      // Update aria-label if it exists
      if (link.hasAttribute('aria-label')) {
        link.setAttribute('aria-label', currentLabel + ' (opens in new tab)');
      }

      // Add visual indicator via CSS class (optional icon)
      link.classList.add('external-link');
    });

    // Add CSS for visual indicator if not already present
    if (!document.getElementById('a11y-external-link-styles')) {
      const style = document.createElement('style');
      style.id = 'a11y-external-link-styles';
      style.textContent = `
        /* Visual indicator for external links (WCAG 3.2.5) */
        .external-link::after {
          content: '';
          display: inline-block;
          width: 0.7em;
          height: 0.7em;
          margin-left: 0.25em;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'/%3E%3Cpolyline points='15 3 21 3 21 9'/%3E%3Cline x1='10' y1='14' x2='21' y2='3'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          vertical-align: middle;
        }

        /* Hide icon for image-only links or buttons */
        .external-link:has(img):not(:has(*:not(img)))::after,
        .external-link[class*="btn"]::after,
        .external-link.button::after,
        .external-link[role="button"]::after {
          display: none;
        }

        /* Screen reader only text */
        .visually-hidden:not(:focus):not(:active) {
          clip: rect(0 0 0 0);
          clip-path: inset(50%);
          height: 1px;
          overflow: hidden;
          position: absolute;
          white-space: nowrap;
          width: 1px;
        }
      `;
      document.head.appendChild(style);
    }

  }

})();
